import { CloseIcon, EditIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  colors,
  deliveryLocationField,
  FieldMapValue,
  getDateString,
  isTransferField,
  makeAllFieldsOptional,
  newbornSexField,
  poundsToLbOz,
  ReportDeliveryArgs,
  reportDeliveryField,
  TextAreaField,
} from '@hb/shared'

import { PopUpMessageContext, useApp } from '@hb/react'
import { useCallback, useContext, useMemo, useState } from 'react'
import { updateAssessmentDeliveryStatus } from '../../../../backend/functions/other'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { formatValue } from '../../../../utils/data'
import { ActionLog } from '../../../ActionLog'
import { DeleteButton } from '../../../Buttons/DeleteButton'
import { SimpleForm, StandaloneInput } from '../../../forms'
import { CollapseHorizontal } from '../../../shared'

export const ReportDeliveryContent = ({ onClose }: { onClose?: () => void }) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const {
    selectedAssessment: { populated: selectedAssessment },
    assessmentId,
  } = useProfile()
  const { deliveredOn, delivery } = selectedAssessment ?? {}
  const {
    updatedBy,
    updatedOn,
    updatedByGroup,
    newbornFname,
    newbornLname,
    newbornWeight,
    lactationClassCompleted,
    lactationClassCompletedOn,
    lactationProblem,
    lactationProblemOn,
    lactationProblemNotes,
    rhogamAdministration,
    rhogamAdministrationOn,
  } = delivery ?? {}
  const [isEditing, setIsEditing] = useState(!deliveredOn && !delivery?.isTransfer)
  const [isTransfer, setIsTransfer] = useState(!!delivery?.isTransfer)
  // const fieldKeys = useMemo(() => Object.keys(fields ?? {}), [fields])
  const hasData = useMemo(() => !!deliveredOn || !!delivery?.isTransfer, [deliveredOn, delivery])
  const { appName } = useApp()
  const handleSubmit = useCallback(
    async (d: FieldMapValue) => {
      if (!assessmentId) {
        return { error: 'Internal error' }
      }

      const { lb, oz } = d.newbornWeight ?? {}
      const submitted: ReportDeliveryArgs = {
        appName,
        assessmentId,
        deliveredOn: d.date ?? null,
        newbornSex: d.newbornSex ?? null,
        newbornWeight: null,
        newbornFname: d.newbornFname ?? null,
        newbornLname: d.newbornLname ?? null,
        summary: d.summary ?? null,
        deliveryTypes: d.deliveryTypes ?? null,
        deliveryLocation: d.deliveryLocation ?? null,
        deliveryLocationOther: d.deliveryLocationOther ?? null,
        isTransfer: d ? isTransfer : false,
        lactationClassCompleted: d.lactationClassCompleted ?? false,
        lactationClassCompletedOn: d.lactationClassCompleted
          ? (d.lactationClassCompletedOn ?? null)
          : null,
        lactationProblem: d.lactationProblem ?? false,
        lactationProblemOn: d.lactationProblem ? (d.lactationProblemOn ?? null) : null,
        lactationProblemNotes: d.lactationProblem ? (d.lactationProblemNotes ?? null) : null,
        rhogamAdministration: d.rhogamAdministration ?? false,
        rhogamAdministrationOn: d.rhogamAdministration ? (d.rhogamAdministrationOn ?? null) : null,
      }
      if (oz && !lb) {
        return processResponse({
          error: 'Please enter pounds for newborn weight',
        })
      }
      if (lb && oz && typeof lb === 'number' && typeof oz === 'number') {
        submitted.newbornWeight = lb + oz / 16
      }
      return updateAssessmentDeliveryStatus(submitted)
        .then(() => {
          // if (onClose) onClose()
          setIsEditing(false)
          return processResponse({ success: 'Report delivery updated' })
        })
        .catch((err: any) => {
          console.error(err)
          return processResponse({
            error: err?.message || 'Error updating report delivery',
          })
        })
    },
    [processResponse, assessmentId, appName, isTransfer],
  )

  const handleDelete = useCallback(() => {
    if (!assessmentId) {
      return { error: 'Internal error' }
    }
    try {
      return updateAssessmentDeliveryStatus({ appName, assessmentId }).then(() => {
        setIsEditing(false)
        return processResponse({ success: 'Delivery/transfer deleted' })
      })
    } catch (err: any) {
      console.error(err)
      return processResponse({
        error: err?.message || 'Error deleting report delivery',
      })
    }
  }, [appName, assessmentId, processResponse])

  const { oz, lb } = useMemo(() => {
    if (!newbornWeight) return { lb: null, oz: null }
    return poundsToLbOz(newbornWeight)
  }, [newbornWeight])
  const value = useMemo(
    () =>
      deliveredOn || delivery?.isTransfer
        ? {
            date: deliveredOn ?? null,
            newbornSex: delivery?.newbornSex,
            deliveryLocation: delivery?.location,
            deliveryLocationOther: delivery?.locationOther,
            newbornFname: delivery?.newbornFname,
            newbornLname: delivery?.newbornLname,
            newbornWeight: {
              lb,
              oz,
            },
            deliveryTypes: delivery?.deliveryTypes,
            lactationClassCompleted: delivery?.lactationClassCompleted ?? false,
            lactationClassCompletedOn: delivery?.lactationClassCompletedOn ?? null,
            lactationProblem: delivery?.lactationProblem ?? false,
            lactationProblemOn: delivery?.lactationProblemOn ?? null,
            lactationProblemNotes: delivery?.lactationProblemNotes ?? null,
            rhogamAdministration: delivery?.rhogamAdministration ?? false,
            rhogamAdministrationOn: delivery?.rhogamAdministrationOn ?? null,
            summary: delivery?.summary,
          }
        : {},
    [deliveredOn, delivery, lb, oz],
  )

  const formattedDate = useMemo(
    () => (deliveredOn && deliveredOn !== Infinity ? getDateString(deliveredOn) : ''),
    [deliveredOn],
  )
  const formattedLactationClassDate = useMemo(
    () => (lactationClassCompletedOn ? getDateString(lactationClassCompletedOn) : ''),
    [lactationClassCompletedOn],
  )
  const formattedLactationProblemDate = useMemo(
    () => (lactationProblemOn ? getDateString(lactationProblemOn) : ''),
    [lactationProblemOn],
  )
  const formattedRhogamDate = useMemo(
    () => (rhogamAdministrationOn ? getDateString(rhogamAdministrationOn) : ''),
    [rhogamAdministrationOn],
  )
  const formattedSex = useMemo(
    () => formatValue({ field: newbornSexField, value: delivery?.newbornSex }),
    [delivery?.newbornSex],
  )
  const formattedLocation = useMemo(
    () =>
      delivery?.location === 'other'
        ? `Other - ${delivery?.locationOther ?? ''}`
        : formatValue({ field: deliveryLocationField, value: delivery?.location }),
    [delivery],
  )
  const field = useMemo(
    () =>
      isTransfer
        ? makeAllFieldsOptional({
            ...reportDeliveryField,
            children: {
              ...reportDeliveryField.children,
              summary: {
                ...reportDeliveryField.children.summary,
                hints: [
                  {
                    text: 'If patient transferred, please include all prenatal dates performed',
                  },
                ],
              } as TextAreaField,
            },
          })
        : reportDeliveryField,
    [isTransfer],
  )

  const showEditMessage = useMemo(() => hasData && appName === 'providers-app', [hasData, appName])

  const editButton = (
    <IconButton
      aria-label="Edit"
      icon={<EditIcon />}
      _hover={{
        bg: 'blackAlpha.200',
      }}
      borderRadius="full"
      onClick={showEditMessage ? undefined : () => setIsEditing(true)}
      size="sm"
      variant="ghost"
      color="white"
    />
  )

  return (
    <Flex w="100%" h="auto" maxH="500px" overflowY="auto" borderRadius={6} flexDir="column">
      <HStack
        borderBottom="1px solid #cdcdcd"
        px={3}
        py={1}
        bg={colors.green.hex}
        spacing={1}
        borderTopRadius={6}>
        <Text
          fontSize="lg"
          fontFamily="Hero-New"
          fontWeight={500}
          textShadow="1px 1px 3px #00000044"
          color="white">
          {deliveredOn ? 'Delivery Info' : 'Report Delivery'}
        </Text>
        <CollapseHorizontal width={40} in={!isEditing && hasData}>
          {showEditMessage ? (
            <Tooltip
              hasArrow
              bg="orange.600"
              label="Contact HB staff via the pregnany chat to report delivery updates">
              {editButton}
            </Tooltip>
          ) : (
            editButton
          )}
        </CollapseHorizontal>
        {onClose ? (
          <IconButton
            aria-label="Close"
            ml="auto"
            color="white"
            borderRadius="full"
            bg="blackAlpha.200"
            _hover={{ bg: 'blackAlpha.300' }}
            size="xs"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        ) : null}
      </HStack>
      <Box overflowY="auto" px={1} flex={1} minH="0" w="100%">
        <Collapse style={{ width: '100%' }} in={!isEditing}>
          <VStack
            px={3}
            spacing={0}
            align="flex-start"
            py={2}
            fontFamily="Hero-New"
            fontSize="0.92rem"
            borderBottomRadius={6}>
            {delivery?.isTransfer ? (
              <Badge fontSize="xs" fontWeight={500}>
                Transfer
              </Badge>
            ) : null}
            <HStack>
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Date of Delivery
              </Text>
              <Text>{formattedDate || 'None'}</Text>
            </HStack>
            <HStack>
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Sex of Newborn
              </Text>
              <Text>{formattedSex || 'None'}</Text>
            </HStack>
            <HStack>
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Newborn First Name
              </Text>
              <Text>{newbornFname || 'None'}</Text>
            </HStack>
            <HStack>
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Newborn Last Name
              </Text>
              <Text>{newbornLname || 'None'}</Text>
            </HStack>
            <Flex gap={1} align="center">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Newborn Weight
              </Text>
              {lb !== null && oz !== null ? (
                <>
                  <Text>{lb} lb</Text>
                  <Text>{oz.toFixed(2)} oz</Text>
                </>
              ) : (
                <Text color="gray.500">None</Text>
              )}
            </Flex>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Delivery Location
              </Text>
              <Text>{formattedLocation || 'None'}</Text>
            </HStack>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Delivery Types
              </Text>
              <HStack>
                {delivery?.deliveryTypes?.length ? (
                  delivery?.deliveryTypes.map(v => (
                    <Badge
                      fontWeight={500}
                      borderRadius="full"
                      bg={colors.green.hex}
                      color="green.50"
                      textShadow="1px 1px 3px #00000077"
                      key={v}
                      px={2}
                      fontSize="xs"
                      fontFamily="Hero-New">
                      {v}
                    </Badge>
                  ))
                ) : (
                  <Text color="gray.500">None</Text>
                )}
              </HStack>
            </HStack>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Lactation Class Completed
              </Text>
              {lactationClassCompleted ? (
                <HStack>
                  <Badge
                    fontWeight={500}
                    borderRadius="full"
                    bg={colors.green.hex}
                    color="green.50"
                    textShadow="1px 1px 3px #00000077"
                    px={2}
                    fontSize="xs"
                    fontFamily="Hero-New">
                    Yes
                  </Badge>
                  {formattedLactationClassDate ? (
                    <Text fontSize="sm">{formattedLactationClassDate}</Text>
                  ) : null}
                </HStack>
              ) : (
                <Text color="gray.500">No</Text>
              )}
            </HStack>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Lactation Problem
              </Text>
              {lactationProblem ? (
                <VStack align="flex-start" spacing={0}>
                  <HStack>
                    <Badge
                      fontWeight={500}
                      borderRadius="full"
                      bg={colors.green.hex}
                      color="green.50"
                      textShadow="1px 1px 3px #00000077"
                      px={2}
                      fontSize="xs"
                      fontFamily="Hero-New">
                      Yes
                    </Badge>
                    {formattedLactationProblemDate ? (
                      <Text fontSize="sm">{formattedLactationProblemDate}</Text>
                    ) : null}
                  </HStack>
                  {lactationProblemNotes ? (
                    <Text fontSize="sm">{lactationProblemNotes}</Text>
                  ) : null}
                </VStack>
              ) : (
                <Text color="gray.500">No</Text>
              )}
            </HStack>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Rhogam Administration
              </Text>
              {rhogamAdministration ? (
                <HStack>
                  <Badge
                    fontWeight={500}
                    borderRadius="full"
                    bg={colors.green.hex}
                    color="green.50"
                    textShadow="1px 1px 3px #00000077"
                    px={2}
                    fontSize="xs"
                    fontFamily="Hero-New">
                    Yes
                  </Badge>
                  {formattedRhogamDate ? <Text fontSize="sm">{formattedRhogamDate}</Text> : null}
                </HStack>
              ) : (
                <Text color="gray.500">No</Text>
              )}
            </HStack>
            <HStack w="100%">
              <Text color="gray.500" fontWeight={500} fontSize="sm">
                Summary
              </Text>
              <Text>{delivery?.summary || 'None'}</Text>
            </HStack>
            <HStack w="100%">
              <ActionLog action="Updated" by={updatedBy} group={updatedByGroup} on={updatedOn} />
              <DeleteButton
                size="xs"
                ml="auto"
                variant="ghost"
                color={colors.red.hex}
                itemName="delivery report"
                onDelete={() => handleDelete()}
              />
            </HStack>
          </VStack>
        </Collapse>
        <Collapse style={{ width: '100%' }} in={isEditing}>
          <Flex w="100%" px={3} pt={2}>
            <Box position="relative" top="2px">
              <StandaloneInput
                style={{ padding: 0 }}
                theme="detailed"
                value={isTransfer}
                onChange={setIsTransfer}
                field={isTransferField}
              />
            </Box>
          </Flex>
          <SimpleForm
            onCancel={deliveredOn ? () => setIsEditing(false) : onClose} //
            boxProps={{ bg: 'transparent', p: 0 }}
            theme="detailed"
            field={field}
            canSubmitClean
            value={value}
            onSubmit={handleSubmit}
          />
        </Collapse>
      </Box>
    </Flex>
  )
}
